import { formatApiErrorDetail } from "@/lib/api";
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AdminShell } from "@/components/Layout";
import {
  Package, UploadSimple, CheckCircle, Warning, XCircle,
  Info, FileArrowUp,
} from "@phosphor-icons/react";

const BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * AdminKraaftImport — bulk-import Kraaft photo/receipt bundles.
 * Same pattern as AdminWhatsAppImport: drag-drop or click, chunked upload
 * for >50MB, folder path (which Kraaft uses per-project) drives the address
 * match, unmatched files go to a picker for manual attach.
 * Route: /admin/kraaft-import
 */
export default function AdminKraaftImport() {
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [progress, setProgress] = useState(0);
  const [pending, setPending] = useState([]);
  const inputRef = useRef(null);
  const abortRef = useRef(null);
  const token = () => localStorage.getItem("fr_token") || localStorage.getItem("token") || "";

  const loadPending = async () => {
    try {
      const r = await axios.get(`${BASE}/api/kraaft-import/unmatched`,
        { headers: { Authorization: `Bearer ${token()}` } });
      setPending(r.data.unmatched || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { loadPending(); }, []);

  // Drag-drop
  const [dragging, setDragging] = useState(false);
  const dragCounter = useRef(0);
  useEffect(() => {
    const onDragEnter = (e) => {
      if (!e.dataTransfer?.types?.includes("Files")) return;
      e.preventDefault();
      dragCounter.current += 1; setDragging(true);
    };
    const onDragOver = (e) => { if (e.dataTransfer?.types?.includes("Files")) e.preventDefault(); };
    const onDragLeave = () => {
      dragCounter.current -= 1;
      if (dragCounter.current <= 0) { dragCounter.current = 0; setDragging(false); }
    };
    const onDrop = (e) => {
      if (!e.dataTransfer?.files?.length) return;
      e.preventDefault();
      dragCounter.current = 0; setDragging(false);
      upload(e.dataTransfer.files[0]);
    };
    window.addEventListener("dragenter", onDragEnter);
    window.addEventListener("dragover", onDragOver);
    window.addEventListener("dragleave", onDragLeave);
    window.addEventListener("drop", onDrop);
    return () => {
      window.removeEventListener("dragenter", onDragEnter);
      window.removeEventListener("dragover", onDragOver);
      window.removeEventListener("dragleave", onDragLeave);
      window.removeEventListener("drop", onDrop);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cancelUpload = () => { abortRef.current?.abort(); };

  const upload = async (file) => {
    if (!file) return;
    if (!/\.zip$/i.test(file.name) && file.type !== "application/zip" && file.type !== "application/x-zip-compressed") {
      toast.error("Must be a .zip file exported from Kraaft");
      return;
    }
    if (file.size === 0) { toast.error("File is empty"); return; }
    setUploading(true); setResult(null); setProgress(0);
    abortRef.current = new AbortController();
    try {
      const useChunked = file.size > 50 * 1024 * 1024;
      const r = useChunked ? await uploadChunked(file) : await uploadSingle(file);
      setResult(r);
      toast.success(`Auto-attached ${r.stats.copied} of ${r.stats.total} file(s)`);
      loadPending();
    } catch (e) {
      const isAborted = e.code === "ERR_CANCELED" || /aborted|canceled/i.test(e.message || "");
      const isTimeout = e.code === "ECONNABORTED" || /timeout/i.test(e.message || "");
      const isSize = e?.response?.status === 413;
      if (isAborted) toast.info("Upload canceled");
      else if (isSize) toast.error("Export too large — please split into smaller zips.");
      else if (isTimeout) toast.error("Upload timed out. Try again.");
      else toast.error(`Import failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setUploading(false); setProgress(0); }
  };

  const uploadSingle = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    const r = await axios.post(`${BASE}/api/kraaft-import/import-zip`, fd, {
      headers: { Authorization: `Bearer ${token()}`, "Content-Type": "multipart/form-data" },
      onUploadProgress: (e) => { if (e.total) setProgress(Math.round((e.loaded / e.total) * 100)); },
      timeout: 900000, signal: abortRef.current?.signal,
    });
    return r.data;
  };

  const uploadChunked = async (file) => {
    const CHUNK = 5 * 1024 * 1024;
    const total = Math.ceil(file.size / CHUNK);
    const init = await axios.post(`${BASE}/api/kraaft-import/import-init`, {},
      { headers: { Authorization: `Bearer ${token()}` }, timeout: 30000, signal: abortRef.current?.signal });
    const importId = init.data.import_id;
    for (let i = 0; i < total; i++) {
      if (abortRef.current?.signal.aborted) throw new Error("Upload canceled");
      const start = i * CHUNK;
      const end = Math.min(start + CHUNK, file.size);
      const fd = new FormData();
      fd.append("import_id", importId);
      fd.append("chunk_index", String(i));
      fd.append("total_chunks", String(total));
      fd.append("chunk", file.slice(start, end), `chunk-${i}.bin`);
      await axios.post(`${BASE}/api/kraaft-import/import-chunk`, fd, {
        headers: { Authorization: `Bearer ${token()}`, "Content-Type": "multipart/form-data" },
        timeout: 120000, signal: abortRef.current?.signal,
      });
      setProgress(Math.round(((i + 1) / total) * 95));
    }
    setProgress(96);
    const fin = await axios.post(`${BASE}/api/kraaft-import/import-finalize`,
      { import_id: importId },
      { headers: { Authorization: `Bearer ${token()}` }, timeout: 900000, signal: abortRef.current?.signal });
    setProgress(100);
    return fin.data;
  };

  return (
    <AdminShell>
      {dragging && (
        <div className="fixed inset-0 z-[9999] pointer-events-none flex items-center justify-center bg-fuchsia-500/20 backdrop-blur-sm border-8 border-dashed border-fuchsia-500"
          data-testid="kr-drag-overlay">
          <div className="bg-white px-8 py-6 border-4 border-fuchsia-500 shadow-2xl text-center">
            <FileArrowUp weight="fill" size={64} className="mx-auto text-fuchsia-600"/>
            <div className="mt-3 font-black uppercase tracking-widest text-xl text-fuchsia-800">
              Drop Kraaft .zip
            </div>
            <div className="mt-1 text-sm text-slate-600">Folder names become WO addresses</div>
          </div>
        </div>
      )}
      <div className="p-6 sm:p-8 max-w-5xl mx-auto" data-testid="kraaft-import-page">
        <div className="flex items-center gap-3">
          <Package weight="fill" size={30} className="text-fuchsia-600"/>
          <div>
            <h1 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tight">
              Kraaft Bulk Import
            </h1>
            <div className="text-sm text-slate-600 font-medium mt-0.5">
              Drop a Kraaft project-photo .zip → auto-attach every photo to the right Work Order using the folder path.
            </div>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 border-2 border-slate-200 bg-white">
            <div className="flex items-center gap-2 font-black uppercase text-[11px] tracking-widest text-slate-700">
              <Info weight="fill" size={14} className="text-fuchsia-600"/> How to export
            </div>
            <ol className="mt-2 text-xs text-slate-700 space-y-1 list-decimal list-inside">
              <li>In Kraaft, select the projects you want to archive</li>
              <li>Choose <b>Export → Photos &amp; Files (.zip)</b></li>
              <li>Kraaft groups each project into its own folder</li>
              <li>Drop the resulting .zip on the right →</li>
            </ol>
            <div className="mt-2 p-2 bg-fuchsia-50 border border-fuchsia-200 text-[11px] text-fuchsia-900 rounded">
              <b>Any size works.</b> Uploads over 50 MB auto-chunk into 5 MB slices. Duplicates auto-skip on re-import.
            </div>
          </div>
          <div className="p-4 border-2 border-slate-200 bg-white">
            <div className="flex items-center gap-2 font-black uppercase text-[11px] tracking-widest text-slate-700">
              <Info weight="fill" size={14} className="text-fuchsia-600"/> How we match
            </div>
            <ul className="mt-2 text-xs text-slate-700 space-y-1 list-disc list-inside">
              <li>Folder path contains a <b>WO#</b> → matched (best)</li>
              <li>Folder name contains a <b>property address</b> → matched</li>
              <li>Fuzzy street name (e.g. "Dorrance") → matched if 1 open WO</li>
              <li>Docs (PDF, receipts) → land in <b>Receipts</b>; photos → <b>Before Photos</b></li>
              <li>Videos + files &gt; 50MB → skipped by default</li>
              <li>Unmatched files stage safely — attach them later from the picker</li>
            </ul>
          </div>
        </div>

        <div className="mt-6">
          <button onClick={() => inputRef.current?.click()} disabled={uploading}
            data-testid="kr-upload-btn"
            className={`w-full h-40 border-4 border-dashed ${uploading ? "border-slate-300 bg-slate-100" : "border-fuchsia-400 bg-fuchsia-50 hover:border-fuchsia-600 hover:bg-fuchsia-100"} flex flex-col items-center justify-center transition disabled:opacity-60`}>
            {uploading ? (
              <>
                <UploadSimple weight="bold" size={40} className="text-slate-500 animate-pulse"/>
                <div className="mt-2 font-black uppercase tracking-widest text-sm text-slate-600">
                  {progress < 96 ? `Uploading… ${progress}%` : "Processing…"}
                </div>
                <div className="mt-1 text-[11px] text-slate-500">
                  {progress < 96 ? "Chunked upload in flight — leave this tab open." : "Matching every file to a Work Order — hang tight."}
                </div>
              </>
            ) : (
              <>
                <FileArrowUp weight="fill" size={44} className="text-fuchsia-600"/>
                <div className="mt-2 font-black uppercase tracking-widest text-sm text-fuchsia-800">
                  Drop Kraaft .zip here
                </div>
                <div className="mt-1 text-[11px] text-slate-600">or click to browse (up to 500 MB)</div>
              </>
            )}
          </button>
          {uploading && (
            <button type="button" onClick={cancelUpload}
              data-testid="kr-cancel-btn"
              className="mt-2 w-full h-9 border-2 border-red-400 hover:bg-red-50 text-red-700 font-black uppercase text-[10px] tracking-widest">
              Cancel upload
            </button>
          )}
          <input ref={inputRef} type="file" accept=".zip,application/zip,application/x-zip-compressed"
            className="hidden" data-testid="kr-file-input"
            onChange={(e) => { if (e.target.files?.[0]) { upload(e.target.files[0]); e.target.value = ""; }}}/>
        </div>

        {pending.length > 0 && (
          <div className="mt-6 p-4 border-2 border-amber-300 bg-amber-50" data-testid="kr-pending-panel">
            <div className="flex items-center gap-2 font-black uppercase text-[11px] tracking-widest text-amber-800">
              <Warning weight="fill" size={14}/> {pending.length} file{pending.length === 1 ? "" : "s"} still waiting for a home
            </div>
            <div className="text-xs text-slate-700 mt-1">
              From earlier imports. Pick an address for each and we'll list every WO at that property.
            </div>
            <div className="mt-3 divide-y-2 divide-slate-100 border-2 border-amber-200 bg-white">
              {pending.map(r => (
                <KrUnmatchedRow key={r.id} row={r}
                  onAttached={(id) => setPending(p => p.filter(x => x.id !== id))}/>
              ))}
            </div>
          </div>
        )}

        {result && <KrResults result={result} onDismiss={loadPending}/>}

        {/* Chat mirror shell — waiting on Kraaft OAuth credentials */}
        <KrChatMirrorPanel/>
      </div>
    </AdminShell>
  );
}


function KrChatMirrorPanel() {
  const [status, setStatus] = useState(null);
  const [body, setBody] = useState("");
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [savingKey, setSavingKey] = useState(false);
  const token = () => localStorage.getItem("fr_token") || localStorage.getItem("token") || "";
  const auth = () => ({ headers: { Authorization: `Bearer ${token()}` } });

  const loadStatus = async () => {
    try {
      const r = await axios.get(`${BASE}/api/kraaft/mirror/status`, auth());
      setStatus(r.data);
    } catch {}
  };
  useEffect(() => { loadStatus(); }, []);

  const saveKey = async () => {
    if (apiKey.trim().length < 20) { toast.error("Paste your full Kraaft API key"); return; }
    setSavingKey(true);
    try {
      await axios.post(`${BASE}/api/kraaft/mirror/save-key`, { api_key: apiKey.trim() }, auth());
      toast.success("Kraaft API key saved — mirror is now armed");
      setApiKey("");
      loadStatus();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Save failed")); }
    finally { setSavingKey(false); }
  };

  const clearKey = async () => {
    if (!window.confirm("Remove the saved Kraaft API key? Mirror will pause until a new one is saved.")) return;
    try {
      await axios.post(`${BASE}/api/kraaft/mirror/clear-key`, {}, auth());
      toast.success("Key removed");
      loadStatus();
    } catch { toast.error("Failed"); }
  };

  const doPreview = async () => {
    if (!body.trim()) { toast.error("Paste a Kraaft chat message first"); return; }
    setPreviewing(true);
    try {
      const r = await axios.post(`${BASE}/api/kraaft/mirror/preview`,
        { sender: "You (test)", body }, auth());
      setPreview(r.data);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Preview failed")); }
    finally { setPreviewing(false); }
  };

  const keyReady = !!status?.api_key_present;

  return (
    <div className="mt-8 space-y-4" data-testid="kr-chat-mirror-panel">
      <div className="p-4 border-2 border-fuchsia-300 bg-fuchsia-50/60">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center gap-1.5 border-2 px-2.5 py-1 font-black uppercase text-[10px] tracking-widest ${
            keyReady ? "bg-emerald-600 border-emerald-700 text-white" : "bg-amber-500 border-amber-600 text-white"
          }`}>
            <div className={`w-2 h-2 ${keyReady ? "bg-white animate-pulse" : "bg-white"}`}/>
            {keyReady ? "MIRROR LIVE" : "AWAITING API KEY"}
          </span>
          <div className="font-black uppercase text-[11px] tracking-widest text-fuchsia-800">Chat mirror · Kraaft → Field Radar</div>
          {keyReady && status?.api_key_hint && (
            <span className="ml-auto text-[10px] text-slate-500 font-mono">key: {status.api_key_hint}</span>
          )}
        </div>
        <div className="mt-2 text-xs text-slate-700">
          Every address-tagged chat message in your Kraaft rolling-WO channel flows into the matched Field Radar WO as a note — with priority + status hints auto-detected. <b>Strict read-only</b> — Kraaft's API doesn't allow sending messages/files back, and we wouldn't anyway.
        </div>

        {!keyReady && (
          <div className="mt-3 space-y-3">
            <div className="p-3 bg-white border border-amber-300 text-xs text-slate-800">
              <div className="font-black uppercase text-[10px] tracking-widest text-amber-800 mb-1">Two-step activation (per Kraaft's official docs):</div>
              <div className="space-y-2 leading-relaxed">
                <div><b>1.</b> Email Kraaft support asking to <b>activate the API on your workspace</b> (only the account <b>owner</b> can request this):</div>
                <pre className="whitespace-pre-wrap font-mono text-[11px] bg-slate-50 p-2 border border-slate-200">
{`Hi — I'd like to activate the Kraaft API on our workspace so we can read our chat records into our internal dashboard. Read-only via the "Administrator & Owner" role key.

Please confirm activation is complete and I'll generate the key from Menu → Settings → API Keys.

Thanks!`}
                </pre>
                <div><b>2.</b> Once Kraaft confirms activation → open Kraaft → <b>Menu → Settings → API Keys</b> → <b>Create an API key</b> → name it <code className="text-[11px] bg-slate-100 px-1 py-0.5">Field Radar</code> → role <b>"Administrator &amp; Owner"</b> → <b>copy the key immediately</b> (Kraaft only shows it once) → paste below.</div>
              </div>
            </div>
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">Kraaft API key</label>
              <div className="mt-1 flex gap-2">
                <input type="password" value={apiKey} onChange={e => setApiKey(e.target.value)}
                  data-testid="kr-mirror-key-input"
                  placeholder="Paste your Kraaft API key here (kept encrypted at rest)"
                  className="flex-1 h-10 px-3 border-2 border-slate-300 text-sm font-mono"/>
                <button onClick={saveKey} disabled={savingKey}
                  data-testid="kr-mirror-save-key-btn"
                  className="h-10 px-4 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase text-xs tracking-widest disabled:opacity-40">
                  {savingKey ? "Saving…" : "Save & go live"}
                </button>
              </div>
            </div>
          </div>
        )}
        {keyReady && (
          <div className="mt-3">
            <button onClick={clearKey}
              data-testid="kr-mirror-clear-key-btn"
              className="text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-rose-600">
              Remove key
            </button>
          </div>
        )}
      </div>

      <div className="p-4 border-2 border-slate-200 bg-white">
        <div className="font-black uppercase text-[11px] tracking-widest text-slate-700 mb-2">
          Parser dry-run (works without OAuth)
        </div>
        <div className="text-xs text-slate-600 mb-2">
          Paste any Kraaft message — the parser shows what it would do to your Field Radar WOs. Zero writes.
        </div>
        <textarea value={body} onChange={e => setBody(e.target.value)}
          data-testid="kr-mirror-preview-input"
          placeholder={`Example:
5922 Osage Ave - Unit 2
Mold on wall and water stain on ceiling

1733 S Dorrance St - 1 (Priority)
SMS and left VM

240 Hermitage St (Return Visit needed)
Faucet still leaking after last visit`}
          className="w-full h-40 p-2 border-2 border-slate-300 text-sm font-mono"/>
        <button onClick={doPreview} disabled={previewing}
          data-testid="kr-mirror-preview-btn"
          className="mt-2 h-9 px-3 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-black uppercase text-[10px] tracking-widest disabled:opacity-40">
          {previewing ? "Parsing…" : "Preview what would happen"}
        </button>
        {preview && (
          <div className="mt-3 p-3 border-2 border-slate-100 bg-slate-50/60">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">
              {preview.blocks} block(s) parsed · {preview.matched} matched to FR WO(s)
            </div>
            <div className="mt-2 space-y-2">
              {preview.actions.map((a, i) => (
                <div key={i} className={`p-2 border-2 ${a.matched_wo_id ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-white"}`} data-testid={`kr-mirror-preview-row-${i}`}>
                  <div className="text-[11px] font-bold text-slate-800">{a.header || "(no address)"}</div>
                  {a.note && <div className="text-[11px] text-slate-600 mt-0.5">{a.note}</div>}
                  <div className="mt-1 flex items-center gap-2 flex-wrap">
                    {a.matched_wo_id ? (
                      <a href={`/admin/jobs/${a.matched_wo_id}`} target="_blank" rel="noreferrer"
                        className="text-[9px] font-black uppercase tracking-widest bg-emerald-600 text-white px-1.5 py-0.5 hover:bg-emerald-700">
                        → {a.matched_address} · {a.match_reason}
                      </a>
                    ) : (
                      <span className="text-[9px] font-black uppercase tracking-widest text-slate-500">no FR WO match</span>
                    )}
                    {a.priority_hint && <span className="text-[9px] font-black uppercase tracking-widest bg-amber-500 text-white px-1.5 py-0.5">→ urgent</span>}
                    {a.status_hint && <span className="text-[9px] font-black uppercase tracking-widest bg-cyan-500 text-white px-1.5 py-0.5">→ {a.status_hint}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


function KrResults({ result, onDismiss }) {
  const [tab, setTab] = useState("matched");
  const [unmatched, setUnmatched] = useState(result.unmatched || []);
  const s = result.stats;
  return (
    <div className="mt-6 space-y-3" data-testid="kr-results">
      <div className="p-4 border-2 border-slate-900 bg-white grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
        <Stat label="Total files" val={s.total} tone="slate"/>
        <Stat label="Auto-logged" val={s.copied} tone="fuchsia"/>
        <Stat label="Unmatched" val={unmatched.length} tone="amber"/>
        <Stat label="Skipped" val={result.skipped.length} tone="slate"/>
      </div>
      <div className="inline-flex border-2 border-slate-200 bg-slate-50">
        {[
          { k: "matched",   label: `Logged (${result.matched.length})`, Icon: CheckCircle, color: "text-fuchsia-700" },
          { k: "unmatched", label: `Unmatched (${unmatched.length})`,   Icon: Warning,     color: "text-amber-700"   },
          { k: "skipped",   label: `Skipped (${result.skipped.length})`, Icon: XCircle,    color: "text-slate-500"   },
        ].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)}
            data-testid={`kr-tab-${t.k}`}
            className={`h-9 px-3 font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1 ${tab === t.k ? "bg-slate-900 text-white" : `${t.color} hover:bg-slate-100`}`}>
            <t.Icon size={12} weight="fill"/> {t.label}
          </button>
        ))}
      </div>
      {tab === "matched" && <KrMatchedList rows={result.matched}/>}
      {tab === "unmatched" && <KrUnmatchedList rows={unmatched} onAttached={(id) => {
        setUnmatched(prev => prev.filter(r => r.id !== id));
        onDismiss?.();
      }}/>}
      {tab === "skipped" && <KrSkippedList rows={result.skipped}/>}
    </div>
  );
}


function Stat({ label, val, tone }) {
  const tones = { slate: "text-slate-700", fuchsia: "text-fuchsia-700", amber: "text-amber-700" };
  return (
    <div>
      <div className={`text-2xl font-black tabular-nums ${tones[tone]}`}>{val}</div>
      <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">{label}</div>
    </div>
  );
}


function KrMatchedList({ rows }) {
  if (!rows.length) return <Empty msg="Nothing auto-logged yet."/>;
  return (
    <div className="divide-y-2 divide-slate-100 border-2 border-slate-200 bg-white" data-testid="kr-matched-list">
      {rows.map((r, i) => (
        <div key={i} className="p-3 flex items-start gap-3 text-sm">
          <CheckCircle weight="fill" size={16} className="text-fuchsia-600 flex-none mt-0.5"/>
          <div className="flex-1 min-w-0">
            <div className="font-mono text-[11px] text-slate-500 truncate">{r.filename}</div>
            {r.folder && <div className="text-[11px] text-slate-600 italic truncate">📁 {r.folder}</div>}
            <div className="mt-1 text-xs text-slate-700 flex flex-wrap items-center gap-2">
              <a href={`/admin/jobs/${r.wo_id}`} className="inline-flex items-center gap-1 border-2 border-fuchsia-500 bg-fuchsia-50 text-fuchsia-800 px-2 py-0.5 font-black uppercase text-[10px] tracking-widest hover:bg-fuchsia-100">
                → WO {r.wo_number || r.wo_id.slice(-4)}
              </a>
              <span className="text-slate-600 truncate">{r.wo_address}</span>
              <span className="text-[9px] font-black uppercase tracking-widest text-fuchsia-700">→ {r.target_field.replace("_", " ")}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}


function KrUnmatchedList({ rows, onAttached }) {
  if (!rows.length) return <Empty msg="Nothing left unmatched — nice work."/>;
  return (
    <div className="divide-y-2 divide-slate-100 border-2 border-amber-200 bg-white" data-testid="kr-unmatched-list">
      <div className="p-3 bg-amber-50 text-xs text-amber-900">
        Pick an address for each file — we'll list every WO at that property.
      </div>
      {rows.map((r, i) => (
        <KrUnmatchedRow key={r.id || i} row={r} onAttached={onAttached}/>
      ))}
    </div>
  );
}


function KrUnmatchedRow({ row, onAttached }) {
  const [addr, setAddr] = useState("");
  const [wos, setWos] = useState([]);
  const [busy, setBusy] = useState(false);
  const token = () => localStorage.getItem("fr_token") || localStorage.getItem("token") || "";

  const loadWos = async (address) => {
    if (!address || address.length < 3) return;
    try {
      const r = await axios.get(`${BASE}/api/whatsapp/wos-at-address?address=${encodeURIComponent(address)}`,
        { headers: { Authorization: `Bearer ${token()}` } });
      setWos(r.data.wos || []);
    } catch { toast.error("Couldn't load WOs at that address"); }
  };

  const attach = async (woId) => {
    setBusy(true);
    try {
      await axios.post(`${BASE}/api/kraaft-import/unmatched/${row.id}/attach`,
        { wo_id: woId, target_field: row.kind === "doc" ? "receipts" : "before_photos" },
        { headers: { Authorization: `Bearer ${token()}` } });
      toast.success("Attached");
      onAttached?.(row.id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Attach failed"));
    } finally { setBusy(false); }
  };

  const dismiss = async () => {
    setBusy(true);
    try {
      await axios.post(`${BASE}/api/kraaft-import/unmatched/${row.id}/dismiss`, {},
        { headers: { Authorization: `Bearer ${token()}` } });
      toast.success("Dismissed");
      onAttached?.(row.id);
    } catch (e) { toast.error("Dismiss failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-3 space-y-2" data-testid={`kr-unmatched-${row.id}`}>
      <div className="flex items-center gap-2 text-sm">
        <Warning weight="fill" size={14} className="text-amber-600"/>
        <div className="flex-1 min-w-0">
          <div className="font-mono text-[11px] text-slate-500 truncate">{row.filename}</div>
          {row.folder && <div className="text-[11px] text-slate-600 italic truncate">📁 {row.folder}</div>}
        </div>
        <button onClick={dismiss} disabled={busy}
          data-testid={`kr-dismiss-${row.id}`}
          className="text-[10px] text-slate-500 hover:text-rose-600 font-black uppercase tracking-widest">Dismiss</button>
      </div>
      <div className="flex gap-2">
        <input list="known-addresses" autoComplete="off" value={addr}
          onChange={e => { setAddr(e.target.value); }}
          onBlur={() => loadWos(addr)}
          placeholder="Type address, e.g. 240 Hermitage"
          className="flex-1 h-9 px-2 border-2 border-slate-300 text-sm"/>
        <button onClick={() => loadWos(addr)}
          className="h-9 px-3 border-2 border-slate-300 hover:bg-slate-100 font-black uppercase text-[10px] tracking-widest">
          Find WOs
        </button>
      </div>
      {wos.length > 0 && (
        <div className="border-2 border-slate-200 bg-slate-50 divide-y divide-slate-200">
          {wos.map(w => (
            <button key={w.id} disabled={busy} onClick={() => attach(w.id)}
              className="w-full p-2 flex items-center gap-2 text-left hover:bg-fuchsia-50 disabled:opacity-40">
              <span className="text-[10px] font-black uppercase tracking-widest bg-slate-900 text-white px-1.5 py-0.5">
                #{w.work_order_number || w.id.slice(-4)}
              </span>
              <span className="text-sm text-slate-800 truncate flex-1">{w.title}</span>
              <span className="text-[9px] font-black uppercase tracking-widest text-slate-500">{w.status}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


function KrSkippedList({ rows }) {
  if (!rows.length) return <Empty msg="Nothing skipped."/>;
  return (
    <div className="divide-y-2 divide-slate-100 border-2 border-slate-200 bg-white" data-testid="kr-skipped-list">
      {rows.map((r, i) => (
        <div key={i} className="p-3 flex items-center gap-3 text-sm">
          <XCircle weight="fill" size={16} className="text-slate-500 flex-none"/>
          <div className="font-mono text-[11px] text-slate-500 flex-1 truncate">{r.filename}</div>
          <span className="text-[10px] text-slate-500 font-black uppercase tracking-widest">{r.reason}</span>
        </div>
      ))}
    </div>
  );
}


function Empty({ msg }) {
  return <div className="p-6 text-center text-sm text-slate-400 border-2 border-dashed border-slate-200">{msg}</div>;
}
